<script lang="ts">
  import { _ } from 'svelte-i18n';
  import Icon from './Icon.svelte';
  
  interface Props {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    showFirstLast?: boolean;
    maxVisible?: number;
  }
  
  let {
    currentPage,
    totalPages,
    onPageChange,
    showFirstLast = true,
    maxVisible = 5
  }: Props = $props();
  
  // Calculate visible page numbers
  const visiblePages = $derived.by(() => {
    const pages: (number | string)[] = [];
    const halfVisible = Math.floor(maxVisible / 2);
    
    let start = Math.max(1, currentPage - halfVisible);
    let end = Math.min(totalPages, currentPage + halfVisible);
    
    // Adjust if we're near the beginning or end
    if (currentPage <= halfVisible) {
      end = Math.min(totalPages, maxVisible);
    } else if (currentPage >= totalPages - halfVisible) {
      start = Math.max(1, totalPages - maxVisible + 1);
    }
    
    // Add first page and ellipsis if needed
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    
    // Add visible page numbers
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    // Add ellipsis and last page if needed
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    
    return pages;
  });
  
  function handlePageClick(page: number | string) {
    if (typeof page === 'number' && page !== currentPage) {
      onPageChange(page);
    }
  }
  
  function goToPrevious() {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  }
  
  function goToNext() {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  }
  
  function goToFirst() {
    if (currentPage !== 1) {
      onPageChange(1);
    }
  }
  
  function goToLast() {
    if (currentPage !== totalPages) {
      onPageChange(totalPages);
    }
  }
</script>

{#if totalPages > 1}
  <nav class="pagination" aria-label={$_('components.pagination.paginationNavigation')}>
    <div class="pagination__container">
      {#if showFirstLast}
        <button
          class="pagination__button pagination__button--nav"
          onclick={goToFirst}
          disabled={currentPage === 1}
          aria-label={$_('components.pagination.goToFirstPage')}
        >
          <Icon name="chevrons-left" size={16} />
        </button>
      {/if}
      
      <button
        class="pagination__button pagination__button--nav"
        onclick={goToPrevious}
        disabled={currentPage === 1}
        aria-label={$_('components.pagination.goToPreviousPage')}
      >
        <Icon name="chevron-left" size={16} />
      </button>
      
      <div class="pagination__pages">
        {#each visiblePages as page}
          {#if page === '...'}
            <span class="pagination__ellipsis">...</span>
          {:else}
            <button
              class="pagination__button pagination__button--page"
              class:pagination__button--active={page === currentPage}
              onclick={() => handlePageClick(page)}
              aria-label={$_('components.pagination.goToPage', { values: { page } })}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </button>
          {/if}
        {/each}
      </div>
      
      <button
        class="pagination__button pagination__button--nav"
        onclick={goToNext}
        disabled={currentPage === totalPages}
        aria-label={$_('components.pagination.goToNextPage')}
      >
        <Icon name="chevron-right" size={16} />
      </button>
      
      {#if showFirstLast}
        <button
          class="pagination__button pagination__button--nav"
          onclick={goToLast}
          disabled={currentPage === totalPages}
          aria-label={$_('components.pagination.goToLastPage')}
        >
          <Icon name="chevrons-right" size={16} />
        </button>
      {/if}
    </div>
    
    <div class="pagination__info">
      {$_('shared.pageOf', { values: { current: currentPage, total: totalPages } })}
    </div>
  </nav>
{/if}

<style>
  .pagination {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin: 2rem 0;
  }
  
  .pagination__container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .pagination__pages {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  
  .pagination__button {
    min-width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-secondary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0.5rem;
  }
  
  .pagination__button:hover:not(:disabled) {
    background: var(--bg-tertiary);
    border-color: var(--border-default);
    color: var(--text-primary);
  }
  
  .pagination__button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .pagination__button--page {
    min-width: 2.5rem;
  }
  
  .pagination__button--nav {
    min-width: 2rem;
    padding: 0.25rem;
  }
  
  .pagination__button--active {
    background: var(--brand-primary);
    border-color: var(--brand-primary);
    color: white;
    font-weight: var(--font-weight-semibold);
  }
  
  .pagination__button--active:hover {
    background: var(--brand-primary-dark);
    border-color: var(--brand-primary-dark);
    color: white;
  }
  
  .pagination__ellipsis {
    padding: 0 0.5rem;
    color: var(--text-tertiary);
    font-size: var(--font-size-sm);
  }
  
  .pagination__info {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
    text-align: center;
  }
  
  /* Mobile responsive */
  @media (max-width: 640px) {
    .pagination__button--nav:first-child,
    .pagination__button--nav:last-child {
      display: none;
    }
    
    .pagination__button {
      min-width: 2rem;
      height: 2rem;
      font-size: var(--font-size-xs);
    }
  }
</style>